import React, { useEffect, useState, Suspense } from 'react';
import {
  ExclamationCircleOutlined,
  DeleteOutlined,
  MoreOutlined,
  PlusOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import {
  getNotificationDefinitions,
  deleteNotificationDefinition,
} from 'store/requests/definitions';
import trTR from 'antd/es/locale/tr_TR';
import 'antd/dist/antd.css';
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Tooltip,
  Dropdown,
  notification,
  ConfigProvider,
  Table,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  ORDER_TYPES,
  STATUS
} from 'helpers/constants';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { PAGE_URLS } from 'helpers/endpoints';

const Add = React.lazy(() => import('./Add'));
const Resend = React.lazy(() => import('./Resend'));
const initialPageParams = {
  order: 'descend',
  column: {
    dataIndex: 'createdAt',
  },
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 50, 100],
    size: 'small',
    current: 1,
    pageSize: 20,
  },
};
const MassNotificationView = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const { confirm } = Modal;
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isResendModelOpen, setIsResendModalOpen] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const [tableParams, setTableParams] = useState(initialPageParams);

  useEffect(() => {
    fetchData(tableParams);
    return () => {
      setLoading(false);
    };
  }, []);

  const fetchData = (tableParamsValue) => {
    const queryString = getQueryString(tableParamsValue);
    getNotificationDefinitionsData(queryString);
  };

  const columns = [
    {
      title: t('parameterProfileCreatedBy'),
      dataIndex: 'createdBy',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('parameterProfileCreatedTime'),
      dataIndex: 'createdAt',
      sorter: true,
      showSorterTooltip: false,
      defaultSortOrder: 'descend',
      render: (dataItem) => {
        if (dataItem) {
          const utcDate = moment.utc(dataItem).add(3, 'hours');
          const formattedDate = utcDate.format('DD-MM-YYYY / HH:mm');

          return formattedDate;
        } else {
          return '--';
        }
      },
    },    
    {
      title: t('notificationMethod'),
      dataIndex: 'notificationType',
      sorter: true,
      showSorterTooltip: false,
      render: (item) => (item ? t(item) : '--'),
    },
    {
      title: t('targetUser'),
      dataIndex: 'recipients',
      sorter: true,
      showSorterTooltip: false,
      render: (item) => recipientsRender(item),
    },
    {
      title: t('notificationText'),
      dataIndex: 'content',
      sorter: true,
      showSorterTooltip: false,
      render: (dataItem) => dataItem.length >50 ?  <Tooltip title={dataItem}>{dataItem.substring(0,50)} ...</Tooltip>: dataItem,
    },
    {
      title: t('notificationHeader'),
      dataIndex: 'header',
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('scheduledDate'),
      dataIndex: 'scheduledTime',
      sorter: true,
      showSorterTooltip: false,
      render: (dataItem) =>
        dataItem ? moment.utc(dataItem).format('DD-MM-YYYY / HH:mm') : '--',
    },
    {
      title: t('status'),
      dataIndex: 'sendStatus',
      sorter: true,
      showSorterTooltip: false,
      render:(dataItem) => dataItem ? t(dataItem) : '--',
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  danger: true,
                  label: t('delete'),
                  icon: <DeleteOutlined />,
                  onClick: () => {
                    showDeleteConfirm(selectedItemId);
                  },
                },
                {
                  label: t('resendNotification'),
                  icon: <RedoOutlined />,
                  onClick: () => {
                    setIsResendModalOpen(true);
                    setSelectedNotificationId(dataItem.id);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItemId(dataItem.id)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  const recipientsRender = (item) => {
    if (!item) {
      return '--';
    }
    const combinedString = [
      ...item.roles.map((role) => t(role)),
      ...item.userId,
    ].join('\n');

    const content = (
      <span key={item.id}>
      {item.roles.map((role) => (
        <span key={role}>{t(role)}<br /></span>
      ))}
        {item.userId.map((user) => (
          <span key={user}>{user}<br /> </span>
        ))}
    </span>
    );

    return (
      combinedString.length > 50 ? <Tooltip title={content}>{combinedString.substring(0, 50)} ...</Tooltip> : content
    );
  }


  const getQueryString = (tableParams) => {
    let queryString = 'filter=deleted==false';

    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    }
    return queryString;
  };

  const getNotificationDefinitionsData = (queryString)  => {
    setLoading(true);
    getNotificationDefinitions(queryString)
      .then((notifications) => {
        const { content, page } = notifications;

        setRows(content ? content : []);
        setTableParams({
          pagination: {
            ...tableParams.pagination,
            total: page.totalElements,
            current: page.number + 1,
            pageSize: page.size,
          },
        });
      })
      .finally(() => setLoading(false));
  }

  const handleTableChange = (pagination, filters, sorter) => {
    fetchData({
      pagination,
      ...sorter,
    });
  }
  const actions = [
    {
      isCrudEnabled,
      type: 'primary',
      title: t('add'),
      icon: <PlusOutlined />,
      onClick: () => setIsAddModalOpen(true),
      disabled: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };

  function showDeleteConfirm(itemId) {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('bonusManagement.bonusOperationDelete'),
      okText: t('delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        deleteNotificationDefinition([itemId]).then((res) => {
          notificationDelete(STATUS.SUCCESS);
          fetchData(initialPageParams);
        }).catch((err) => {
          console.log('err', err);
          notification.error({ message: err.message });
        });
      },
      onCancel() { },
    });
  }

  const notificationDelete = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('bonusManagement.deletedOperation'),
    });
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <Col>{renderActionButtons()}</Col>
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isAddModalOpen && (
          <Add
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={() => fetchData(initialPageParams)}
          />
        )}
        {isResendModelOpen && (
          <Resend
            notificationDefinitionId={selectedNotificationId}
            onCloseHandler={() => setIsResendModalOpen(false)}
            onSuccessCallback={() => fetchData(initialPageParams)}
          />
        )}
      </Suspense>
    </div>
  );
};

export default MassNotificationView;
